import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getAllProducts } from '../services/productService';
import { getBrands } from '../services/brandService';
import { useMarket } from '../context/MarketContext';
import { useReviews } from '../context/ReviewsContext';
import ProductGrid from '../components/product/ProductGrid';
import PriceFilter, { priceBounds } from '../components/shop/PriceFilter';
import { typeKey, priceOf, searchText, typeGroups, typesForParam, hasSkinTypes } from '../utils/catalog';

const SKIN_TYPES    = ['Oily','Dry','Combination','Sensitive','Normal','Acne-Prone'];
const RATINGS       = [4,3,2];
const SORT_OPTIONS  = [
  { label: 'Featured',            value: 'popularity' },
  { label: 'Price: Low → High',   value: 'price_asc' },
  { label: 'Price: High → Low',   value: 'price_desc' },
  { label: 'Newest First',        value: 'newest' },
  { label: 'Highest Discount',    value: 'discount' },
];
const ITEMS_PER_PAGE = 12;

/* ── Accordion filter group ── */
const FilterGroup = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border last:border-0 py-3">
      <button
        className="flex items-center justify-between w-full text-left py-1"
        onClick={() => setOpen(!open)}
      >
        <span className="text-sm font-semibold text-ink">{title}</span>
        <svg className={`w-4 h-4 text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`}
             fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      {open && <div className="mt-2.5">{children}</div>}
    </div>
  );
};

/* ── Custom checkbox ── */
const FilterCheckbox = ({ checked, onChange, label, count }) => (
  <label className="flex items-center gap-2.5 cursor-pointer group py-1" onClick={onChange}>
    <div
      className={`w-4 h-4 rounded flex items-center justify-center border transition-all flex-shrink-0
        ${checked ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'}`}
    >
      {checked && (
        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
        </svg>
      )}
    </div>
    <span className="text-sm text-ink-soft group-hover:text-ink transition-colors flex-1">{label}</span>
    {count !== undefined && <span className="text-xs text-ink-muted">{count}</span>}
  </label>
);

const ShopPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentMarket } = useMarket();
  const { ratingFor, anyRated } = useReviews();
  const marketCode = currentMarket?.isDefault ? null : currentMarket?.code;
  const [filterOpen,   setFilterOpen]   = useState(false);
  const [sortBy,       setSortBy]       = useState('popularity');
  const [page,         setPage]         = useState(1);
  const [showBackTop,  setShowBackTop]  = useState(false);
  const [products,     setProducts]     = useState([]);
  const [brands,       setBrands]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const topRef = useRef(null);

  const [selectedBrands,    setSelectedBrands]    = useState(() => { const b = searchParams.get('brand'); return b ? [b] : []; });
  const [pickedTypes,       setPickedTypes]       = useState(null);   // null = what the link (?type=…) asks for
  const [selectedSkinTypes, setSelectedSkinTypes] = useState(() => { const s = searchParams.get('skinType'); return s ? [s] : []; });
  const [priceLimit,        setPriceLimit]        = useState(null);   // null = any price
  const [minRating,         setMinRating]         = useState(0);
  const [keyword,           setKeyword]           = useState(() => searchParams.get('q') || '');

  const typeParam = searchParams.get('type');
  const qParam = searchParams.get('q');
  const skinParam = searchParams.get('skinType');

  // Redirect legacy ?brand= URLs to /collections/:handle
  useEffect(() => {
    const brand = searchParams.get('brand');
    if (brand) {
      navigate(`/collections/${brand}`, { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      // First chunk paints immediately (spinner off); the remainder is merged in when it lands
      getAllProducts({
        marketCode,
        onFirstChunk: (first) => {
          if (cancelled) return;
          setProducts(first);
          setLoading(false);
        },
      }),
      getBrands(),
    ]).then(([prods, brnds]) => {
      if (cancelled) return;
      setProducts(prods);
      setBrands(brnds);
      // Resolve brand name from slug now that brands are loaded
      const brandSlug = searchParams.get('brand');
      if (brandSlug && prods.length) {
        const found = brnds.find((br) => br.slug === brandSlug);
        if (found) setSelectedBrands([found.name]);
      }
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [marketCode]); // eslint-disable-line

  useEffect(() => {
    const fn = () => setShowBackTop(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Header, footer and home links change ?type= / ?q= while this page stays open: follow them
  useEffect(() => { setPickedTypes(null); setPage(1); }, [typeParam]);
  useEffect(() => { setKeyword(qParam || ''); setPage(1); }, [qParam]);

  // Type, price and searchable text worked out once per product, not on every keystroke or slider move
  const catalog = useMemo(() => products.map((p) => ({ p, type: typeKey(p), price: priceOf(p), text: searchText(p) })), [products]);
  const groups = useMemo(() => typeGroups(products), [products]);
  const typeLabel = (key) => groups.find((g) => g.key === key)?.label ?? key;
  const { floor: priceFloor, ceiling: priceCeiling } = useMemo(() => priceBounds(catalog.map((c) => c.price)), [catalog]);
  const skinTypesKnown = useMemo(() => hasSkinTypes(products), [products]);

  const selectedTypes = useMemo(() => pickedTypes ?? typesForParam(typeParam, groups), [pickedTypes, typeParam, groups]);
  const setSelectedTypes = (next) => setPickedTypes(typeof next === 'function' ? next(selectedTypes) : next);

  const toggle = (value, list, setList) => {
    setList(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    setPage(1);
  };

  const filtered = useMemo(() => {
    let r = catalog;
    const kw = keyword.trim().toLowerCase();
    if (kw)                       r = r.filter(c => c.text.includes(kw));
    if (selectedBrands.length)    r = r.filter(c => selectedBrands.includes(c.p.vendor));
    if (selectedTypes.length)     r = r.filter(c => selectedTypes.includes(c.type));
    if (skinTypesKnown && selectedSkinTypes.length) r = r.filter(c => c.p.skinType.some(s => selectedSkinTypes.includes(s)));
    if (priceLimit != null)       r = r.filter(c => c.price <= priceLimit);
    if (minRating > 0)            r = r.filter(c => (ratingFor(c.p.id)?.average ?? 0) >= minRating);
    const sorted = [...r];
    switch (sortBy) {
      case 'price_asc':  sorted.sort((a,b) => a.price - b.price); break;
      case 'price_desc': sorted.sort((a,b) => b.price - a.price); break;
      // isNew is rarely set; a higher id is a more recently added product
      case 'newest':     sorted.sort((a,b) => ((b.p.isNew?1:0) - (a.p.isNew?1:0)) || (Number(b.p.id) - Number(a.p.id))); break;
      case 'discount':   sorted.sort((a,b) => ((b.p.compareAtPrice||0) - b.price) - ((a.p.compareAtPrice||0) - a.price)); break;
      default:           sorted.sort((a,b) => (b.p.isBestseller?1:0) - (a.p.isBestseller?1:0));
    }
    return sorted.map(c => c.p);
  }, [catalog, selectedBrands, selectedTypes, selectedSkinTypes, skinTypesKnown, priceLimit, minRating, sortBy, keyword, ratingFor]);

  const totalPages  = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated   = filtered.slice((page-1)*ITEMS_PER_PAGE, page*ITEMS_PER_PAGE);
  const shownSkinTypes = skinTypesKnown ? selectedSkinTypes : [];
  const activeCount = selectedBrands.length + selectedTypes.length + shownSkinTypes.length + (minRating>0?1:0) + (priceLimit != null ? 1 : 0) + (keyword?1:0);

  const clearAll = () => {
    setSelectedBrands([]); setPickedTypes([]); setSelectedSkinTypes([]);
    setPriceLimit(null); setMinRating(0); setKeyword(''); setPage(1);
    if (typeParam || qParam || skinParam) navigate('/shop', { replace: true });   // a refresh must not bring them back
  };

  // A plain element, not a component defined in here: a component made on every render is thrown away and rebuilt
  // each time the page updates, which froze the price slider mid-drag and took the focus out of the search box.
  const filterContent = (
    <div>
      {activeCount > 0 && (
        <button onClick={clearAll} className="w-full flex items-center justify-center gap-1.5 text-xs text-accent font-semibold py-2 px-3 bg-accent-light rounded-xl mb-4 hover:bg-accent hover:text-white transition-all">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          Clear all filters ({activeCount})
        </button>
      )}

      {/* Keyword search */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            value={keyword}
            onChange={e => { setKeyword(e.target.value); setPage(1); }}
            placeholder="Search products..."
            className="w-full border border-border rounded-xl px-3 py-2.5 pr-8 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
          />
          {keyword ? (
            <button onClick={() => { setKeyword(''); setPage(1); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-muted hover:text-primary transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          ) : (
            <svg className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          )}
        </div>
      </div>

      <FilterGroup title="Brand">
        <div className="max-h-52 overflow-y-auto space-y-0.5 pr-1 scrollbar-hide">
          {brands.slice(0,12).map(b => (
            <FilterCheckbox
              key={b.id}
              checked={selectedBrands.includes(b.name)}
              onChange={() => toggle(b.name, selectedBrands, setSelectedBrands)}
              label={b.name}
              count={b.productCount}
            />
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title="Price">
        <div className="px-1">
          <PriceFilter floor={priceFloor} ceiling={priceCeiling} limit={priceLimit}
            symbol={currentMarket?.currencySymbol ?? '₹'}
            onCommit={(val) => { setPriceLimit(val); setPage(1); }} />
        </div>
      </FilterGroup>

      {groups.length > 0 && (
        <FilterGroup title="Product Type">
          <div className="space-y-0.5">
            {groups.map(g => (
              <FilterCheckbox key={g.key} checked={selectedTypes.includes(g.key)} onChange={() => toggle(g.key, selectedTypes, setSelectedTypes)} label={g.label} count={g.count} />
            ))}
          </div>
        </FilterGroup>
      )}

      {/* only when products have skin types filled in — otherwise every choice shows nothing */}
      {skinTypesKnown && <FilterGroup title="Skin Type">
        <div className="flex flex-wrap gap-1.5">
          {SKIN_TYPES.map(st => (
            <button key={st} onClick={() => { toggle(st, selectedSkinTypes, setSelectedSkinTypes); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                ${selectedSkinTypes.includes(st) ? 'bg-primary text-white border-primary' : 'border-gray-200 text-ink-soft hover:border-primary hover:text-primary'}`}>
              {st}
            </button>
          ))}
        </div>
      </FilterGroup>}

      {/* only once the store shows real ratings */}
      {anyRated && <FilterGroup title="Min. Rating" defaultOpen={false}>
        <div className="space-y-2">
          {RATINGS.map(r => (
            <label key={r} className="flex items-center gap-2.5 cursor-pointer">
              <input type="radio" checked={minRating===r} onChange={() => { setMinRating(r); setPage(1); }} className="accent-primary" />
              <div className="flex items-center gap-0.5">
                {Array.from({length:r}).map((_,i)=><span key={i} className="text-amber-400 text-sm">★</span>)}
                <span className="text-xs text-ink-soft ml-1">& above</span>
              </div>
            </label>
          ))}
          {minRating > 0 && <button onClick={() => setMinRating(0)} className="text-xs text-accent hover:underline">Clear</button>}
        </div>
      </FilterGroup>}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Shop All Products — Skinn Thrive</title>
        <meta name="description" content="Shop authentic derma skincare products — sunscreen, moisturizers, serums, haircare and more from trusted derma brands." />
        <link rel="canonical" href={`${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/shop`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Skinn Thrive" />
        <meta property="og:title" content="Shop All Products — Skinn Thrive" />
        <meta property="og:description" content="Shop authentic derma skincare products — sunscreen, moisturizers, serums, haircare and more from trusted derma brands." />
        <meta property="og:url" content={`${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/shop`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Shop All Products — Skinn Thrive" />
        <meta name="twitter:description" content="Shop authentic derma skincare products — sunscreen, moisturizers, serums and more from top brands." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" ref={topRef}>
        {/* Page header */}
        <div className="mb-4">
          <h1 className="display-title text-3xl md:text-4xl">Shop All Products</h1>
          <p className="text-ink-soft text-sm mt-1">Authentic dermatologist-recommended skincare &amp; haircare</p>
        </div>

        {/* Category quick-filter pills */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 mb-4 -mx-1 px-1">
          <button
            onClick={() => { setSelectedTypes([]); setPage(1); }}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-all whitespace-nowrap
              ${selectedTypes.length === 0 ? 'bg-primary text-white border-primary shadow-sm' : 'border-border text-ink-soft hover:border-primary hover:text-primary bg-white'}`}
          >
            All
          </button>
          {groups.map(({ key: t, label }) => (
            <button
              key={t}
              onClick={() => { setSelectedTypes([t]); setPage(1); }}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-all whitespace-nowrap
                ${selectedTypes.includes(t) && selectedTypes.length === 1 ? 'bg-primary text-white border-primary shadow-sm' : 'border-border text-ink-soft hover:border-primary hover:text-primary bg-white'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Active filter chips */}
        {activeCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedBrands.map(b => (
              <span key={b} className="inline-flex items-center gap-1.5 bg-primary-light text-primary text-xs font-semibold px-3 py-1.5 rounded-full">
                {b}
                <button onClick={() => toggle(b, selectedBrands, setSelectedBrands)} className="hover:text-primary-dark">✕</button>
              </span>
            ))}
            {selectedTypes.map(t => (
              <span key={t} className="inline-flex items-center gap-1.5 bg-primary-light text-primary text-xs font-semibold px-3 py-1.5 rounded-full">
                {typeLabel(t)} <button onClick={() => toggle(t, selectedTypes, setSelectedTypes)} aria-label={`Remove ${typeLabel(t)}`}>✕</button>
              </span>
            ))}
            {priceLimit != null && (
              <span className="inline-flex items-center gap-1.5 bg-primary-light text-primary text-xs font-semibold px-3 py-1.5 rounded-full">
                Up to {currentMarket?.currencySymbol ?? '₹'}{priceLimit.toLocaleString('en-IN')}
                <button onClick={() => { setPriceLimit(null); setPage(1); }} aria-label="Remove price limit">✕</button>
              </span>
            )}
            {keyword && (
              <span className="inline-flex items-center gap-1.5 bg-primary-light text-primary text-xs font-semibold px-3 py-1.5 rounded-full">
                “{keyword}”
                <button onClick={() => { setKeyword(''); setPage(1); if (qParam) navigate(typeParam ? `/shop?type=${encodeURIComponent(typeParam)}` : '/shop', { replace: true }); }} aria-label="Remove search">✕</button>
              </span>
            )}
            {shownSkinTypes.map(st => (
              <span key={st} className="inline-flex items-center gap-1.5 bg-primary-light text-primary text-xs font-semibold px-3 py-1.5 rounded-full">
                {st} Skin <button onClick={() => toggle(st, selectedSkinTypes, setSelectedSkinTypes)}>✕</button>
              </span>
            ))}
          </div>
        )}

        {/* Sticky results bar */}
        <div className="flex items-center justify-between gap-3 mb-5 py-3 border-b border-border sticky top-16 bg-white/95 backdrop-blur-sm z-30 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <p className="text-sm font-medium text-ink-soft whitespace-nowrap">
            <span className="font-bold text-ink">{filtered.length}</span> product{filtered.length !== 1 ? 's' : ''}<span className="hidden sm:inline"> found</span>
          </p>
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile filter button */}
            <button
              onClick={() => setFilterOpen(true)}
              className="md:hidden flex items-center gap-2 border border-border rounded-xl px-3.5 py-2 text-sm font-medium text-ink-soft hover:border-primary hover:text-primary transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
              </svg>
              Filters
              {activeCount > 0 && (
                <span className="w-5 h-5 text-[10px] font-bold bg-primary text-white rounded-full flex items-center justify-center">{activeCount}</span>
              )}
            </button>
            <select
              value={sortBy}
              onChange={e => { setSortBy(e.target.value); setPage(1); }}
              className="min-w-0 border border-border rounded-xl px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:border-primary transition-all"
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="flex gap-7">
          {/* Desktop sidebar */}
          <aside className="hidden md:block w-56 shrink-0">
            <div className="sticky top-32 bg-white rounded-2xl border border-border p-4" style={{boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
              <h2 className="text-xs font-bold uppercase tracking-widest text-ink-muted mb-4">Filter By</h2>
              {filterContent}
            </div>
          </aside>

          {/* Product grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="text-center py-16 text-gray-400">Loading products...</div>
            ) : (
              <>
                <ProductGrid
                  products={paginated}
                  emptyMessage="No products match your filters. Try removing some filters."
                />
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-1 mt-10 flex-wrap">
                    <button
                      onClick={() => { setPage(p => Math.max(1, p-1)); window.scrollTo({top:0,behavior:'smooth'}); }}
                      disabled={page===1}
                      className="px-3 py-2 text-sm font-medium disabled:opacity-40 text-ink-soft hover:text-primary transition-colors"
                    >
                      ← Prev
                    </button>
                    {(() => {
                      const pages = [];
                      for (let i = 1; i <= totalPages; i++) {
                        if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
                          pages.push(i);
                        } else if (pages[pages.length - 1] !== '…') {
                          pages.push('…');
                        }
                      }
                      return pages.map((p, idx) => p === '…' ? (
                        <span key={`e${idx}`} className="w-9 h-9 flex items-center justify-center text-ink-muted text-sm">…</span>
                      ) : (
                        <button key={p} onClick={() => { setPage(p); window.scrollTo({top:0,behavior:'smooth'}); }}
                          className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all
                            ${p===page ? 'bg-primary text-white shadow-sm' : 'text-ink-soft hover:text-primary hover:bg-primary-light'}`}>
                          {p}
                        </button>
                      ));
                    })()}
                    <button
                      onClick={() => { setPage(p => Math.min(totalPages, p+1)); window.scrollTo({top:0,behavior:'smooth'}); }}
                      disabled={page===totalPages}
                      className="px-3 py-2 text-sm font-medium disabled:opacity-40 text-ink-soft hover:text-primary transition-colors"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter bottom sheet */}
      {filterOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm animate-fade-in" onClick={() => setFilterOpen(false)} />
          <div className="fixed inset-x-0 bottom-0 bg-white z-50 rounded-t-3xl max-h-[85vh] flex flex-col shadow-2xl animate-slide-up">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <h3 className="font-bold text-ink text-base">Filters {activeCount > 0 && <span className="text-primary">({activeCount})</span>}</h3>
              <button onClick={() => setFilterOpen(false)} className="w-8 h-8 rounded-xl bg-surface flex items-center justify-center text-ink-soft hover:bg-primary-light hover:text-primary transition-all">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-4">
              {filterContent}
            </div>
            <div className="px-5 pb-6 pt-3 border-t border-border shrink-0">
              <button onClick={() => setFilterOpen(false)} className="btn-primary w-full py-3.5">
                Show {filtered.length} Product{filtered.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Back to top */}
      {showBackTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-11 h-11 bg-primary text-white rounded-full shadow-lg shadow-primary/30 flex items-center justify-center hover:bg-primary-dark hover:shadow-xl transition-all z-40 animate-fade-in"
          aria-label="Back to top"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7"/>
          </svg>
        </button>
      )}
    </>
  );
};

export default ShopPage;
